import { Bar, BarChart, ResponsiveContainer, XAxis } from "recharts";
import { cn } from "@/design-system/lib/utils";
import { stageFill, type StageSegment } from "./tileBits";

/**
 * The stage ladder as one stacked bar, plus a legend that is also the control.
 *
 * <p>🔴 <b>The graphic is a control, not a decoration.</b> Clicking a segment
 * applies that stage to the table below — the interaction the deal-flow metrics
 * module already runs through <c>buildPipelineDrillThroughUrl</c>, and the
 * reason a reader who spots a bulge can act on it rather than going back to the
 * filter bar to describe what they just saw.</p>
 *
 * <p>🔴 <b>The legend is a parallel run of real <c>&lt;button&gt;</c>s hitting
 * the SAME handler</b>, which is what makes the drill-through keyboard- and
 * screen-reader-reachable: an SVG <c>&lt;rect&gt;</c> with an <c>onClick</c> is
 * reachable by pointer alone. <c>AggregateConcentrationGrid</c> established the
 * pattern; this is the same shape.</p>
 */
export function StageDistributionTile({
  title,
  segments,
  selectedKey,
  onSelect,
  loading,
}: {
  title: string;
  segments: StageSegment[];
  /** The rung currently filtering the table, if any. */
  selectedKey?: string;
  /** Called with the rung's key, or with `null` when the active one is clicked
   *  again — a segment is a toggle rather than a one-way trip that needs the
   *  filter bar to undo. */
  onSelect: (key: string | null) => void;
  loading?: boolean;
}) {
  const total = segments.reduce((sum, s) => sum + s.count, 0);

  // ONE datum, one bar per rung, stacked — the shape that makes a distribution
  // read as a proportion of a whole rather than as five unrelated columns.
  const datum = segments.reduce<Record<string, number>>(
    (row, s) => ({ ...row, [s.key]: s.count }),
    {},
  );

  return (
    <section aria-label={title} data-testid="stage-distribution">
      <h3 className="font-display text-label font-semibold uppercase tracking-label text-gray-mid m-0 mb-space-2">
        {title}
      </h3>

      {loading ? (
        <div className="h-space-5 rounded-md bg-white-warm" aria-hidden />
      ) : total === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          Nothing in this filter set yet.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={28}>
          <BarChart data={[datum]} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <XAxis type="number" hide domain={[0, total]} />
            {segments.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                stackId="ladder"
                fill={stageFill(s.tone)}
                cursor="pointer"
                // Muted while a SIBLING rung is the active filter, so the bar
                // shows which slice the table is currently showing.
                fillOpacity={selectedKey && selectedKey !== s.key ? 0.25 : 1}
                onClick={() => onSelect(selectedKey === s.key ? null : s.key)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}

      <ul className="m-0 mt-space-2 p-0 list-none flex flex-wrap gap-x-space-3 gap-y-space-1">
        {segments.map((s) => {
          const active = selectedKey === s.key;
          return (
            <li key={s.key}>
              <button
                type="button"
                aria-pressed={active}
                onClick={() => onSelect(active ? null : s.key)}
                data-testid={`stage-segment-${s.key}`}
                className={cn(
                  "inline-flex items-center gap-space-1 rounded-pill px-space-2 py-[2px]",
                  "text-caption transition-colors duration-fast ease-standard",
                  "focus-visible:outline-none focus-visible:shadow-goldRing",
                  active ? "bg-onyx/10 text-onyx font-semibold" : "text-gray-mid hover:text-onyx",
                )}
              >
                <span
                  aria-hidden
                  className="inline-block h-[8px] w-[8px] rounded-full"
                  style={{ backgroundColor: stageFill(s.tone) }}
                />
                <span className="truncate max-w-[12rem]">{s.label}</span>
                <span className="tabular-nums">{s.count}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
